import { HOUR_IN_MS, paramsToQuery } from '@/base/MaUtils.js'
import { cacheRequest, makeRequest } from '@/base/MaRequestPooler.js'
import { maLogger } from '@/base/logger'
import { MalformedParamException } from '@/base/constants/Errors.js'

/**
 * Returns user's all keyword groups.
 * @param trackId
 * @param countryCode
 * @returns {Promise<*>} List of keyword groups.
 */
export function getUserKeywordGroups({ trackId, countryCode }) {
  return makeRequest(`/user-keyword-group/v2/app/${trackId}/${countryCode}`)
}

/**
 * Returns added keyword group.
 * @param trackId
 * @param countryCode
 * @param groupName
 * @param sortIndex
 * @param colorCode
 * @returns {Promise<*>} single keyword group.
 */
export function addUserKeywordGroup({ trackId, countryCode, groupName, sortIndex, colorCode }) {
  const params = paramsToQuery({
    groupName,
    sortIndex,
    colorCode,
  })
  return makeRequest(
    `/user-keyword-group/v2/app/${trackId}/${countryCode}?${params}`,
    { method: 'POST' },
  )
}

/**
 * Returns updated keyword group.
 * @param userKeywordGroupId
 * @param isPublic
 * @param isEditable
 * @param groupName
 * @param sortIndex
 * @param colorCode
 * @returns {Promise<*>} single keyword group.
 */
export function updateUserKeywordGroup({
  userKeywordGroupId,
  isPublic,
  isEditable,
  groupName,
  sortIndex,
  colorCode,
}) {
  const params = paramsToQuery({
    isPublic,
    isEditable,
    groupName,
    sortIndex,
    colorCode,
  })
  return makeRequest(
    `/user-keyword-group/v2/${userKeywordGroupId}?${params}`,
    { method: 'PUT' },
  )
}

/**
 * The relevant group is deleted according to the given groupId information.
 * (Words related to this group are not deleted, the relevant group is removed).
 * @param userKeywordGroupId
 * @returns {Promise<*>}
 */
export function deleteUserKeywordGroup({ userKeywordGroupId }) {
  return makeRequest(
    `/user-keyword-group/v2/${userKeywordGroupId}`,
    { method: 'DELETE' },
  )
}

/**
 * The selected word is added to the selected group.
 * @param userKeywordGroupId
 * @param countryCode
 * @param keyword
 * @param keywordCountryCode
 * @returns {Promise<*>} single keyword group.
 */
export function addKeywordToUserKeywordGroup({
  userKeywordGroupId,
  countryCode,
  keyword,
  keywordCountryCode,
}) {
  const params = paramsToQuery({
    keyword,
    countryCode: keywordCountryCode,
  })
  return makeRequest(
    `/user-keyword-group/v2/${userKeywordGroupId}/${countryCode}?${params}`,
    { method: 'POST' },
  )
}

/**
 * The selected bulk words are added to the selected group.
 * @param userKeywordGroupId
 * @param countryCode
 * @param keyword
 * @returns {Promise<*>} single keyword group.
 */
export function addBulkKeywordToUserKeywordGroup({
  userKeywordGroupId,
  countryCode,
  keywords,
}) {
  const query = paramsToQuery({ countryCode })

  return makeRequest(`/user-keyword-group/v2/bulk/${userKeywordGroupId}/${countryCode}?${query}`, {
    method: 'POST',
    data: keywords,
  })
}

/**
 * The selected bulk words are removed to the selected group.
 * @param userKeywordGroupId
 * @param countryCode
 * @param keyword
 * @returns {Promise<*>} single keyword group.
 */
export function deleteBulkKeywordToUserKeywordGroup({
  userKeywordGroupId,
  countryCode,
  keywords,
}) {
  return makeRequest(`/user-keyword-group/v2/bulk/${userKeywordGroupId}/${countryCode}`, {
    method: 'DELETE',
    data: keywords,
  })
}

/**
 * The selected word is removed from the selected group.
 * @param userKeywordGroupId
 * @param countryCode
 * @param keyword
 * @param keywordCountryCode
 * @returns {Promise<*>} single keyword group.
 */
export function deleteKeywordFromUserKeywordGroup({
  userKeywordGroupId,
  countryCode,
  keyword,
  keywordCountryCode,
}) {
  const params = paramsToQuery({
    keyword,
    countryCode: keywordCountryCode,
  })
  return makeRequest(
    `/user-keyword-group/v2/${userKeywordGroupId}/${countryCode}?${params}`,
    { method: 'DELETE' },
  )
}

export function addKeywords({ trackId, countryCode, device, groupId, onboarding }, keywords) {
  const url = `/account/app/${trackId}/keywords/${countryCode}/v2?${paramsToQuery({
    device,
    groupId,
    onboarding,
  })}`
  let postData = []
  if (Array.isArray(keywords)) {
    postData = JSON.stringify(keywords)
  }
  else if (typeof keywords === 'string') {
    postData = JSON.stringify([keywords])
  }
  return makeRequest(url, {
    method: 'POST',
    data: postData,
  })
}

export function getKeywordRankingsWithMetadata({ trackId, countryCode, device, keywords, startDate, endDate }) {
  let data = null
  if (Array.isArray(keywords)) {
    data = keywords
  }
  else if (typeof keywords === 'string') {
    data = keywords.split(',')
  }
  else if (typeof keywords !== 'undefined') {
    maLogger.warn('Invalid keywords provided for getKeywordRankingsWithMetadata', trackId, countryCode, keywords)
  }
  return makeRequest(`/account/app/${trackId}/keywords/ranking/${countryCode}?${paramsToQuery({
    device,
    startDate,
    endDate,
  })}`, {
    method: 'POST',
    data,
  })
}

export function getKeywordDifficultyScores({ keywords, countryCode, store }) {
  return makeRequest(`/keyword/difficulty-score/${store}/country/${countryCode}`, {
    method: 'POST',
    data: keywords,
  })
}

export function getKeywordsRunningSearchAds({ countryCode, startDate, endDate, keywords }, axiosCfg) {
  return makeRequest(`/keyword/sa-stats/impressions/${countryCode}?${paramsToQuery({ startDate, endDate })}`, {
    ...axiosCfg,
    method: 'POST',
    data: keywords,
  })
}

export function getKeywordSpot({ trackId, countryCode }) {
  return makeRequest(`/account/store-account/app/${trackId}/data/keyword-spot?${paramsToQuery({ countryCode })}`)
}

/**
 * Returns the relevancy score for each given keywords.
 * @param {string} trackId - App trackId
 * @param {string} competitorIds - Comppetitor's apps trackIds
 * @param {string} countryCode - Country Code (US,TR, BE etc.)
 * @param {string} store - play/ios
 * @param {Array<string>} keywords - To be calculated keywords
 * @return {Promise<any>} Promise<any>
 */
export function getKeywordRelevancyScore({ trackId, competitorIds, countryCode, store, keywords }) {
  return makeRequest(`/datascience/relevancy/${store}/app/${trackId}?${paramsToQuery({
    country: countryCode,
    competitorIds,
  })}`, {
    method: 'POST',
    data: keywords,
  })
}

/**
 * Returns keyword suggestions based on competitors.
 * @param trackId
 * @param countryCode
 * @param competitors
 * @param trackedCompetitors bool optional, whether or not to include tracked competitors
 * @returns {Promise<any>}
 */
export function getSuggestions({ trackId, countryCode, competitors, trackedCompetitors = false }, axiosCfg) {
  return makeRequest(`/account/app/${trackId}/keywords/suggestion/competitor/${countryCode}?competitors=${competitors}&trackedCompetitors=${trackedCompetitors}`, axiosCfg)
}

export function getSuggestionKeywords({ trackId, countryCode, competitorTrackIds }) {
  return makeRequest(`/app/${trackId}/keywords/suggestion/competitor/${countryCode}?competitors=${encodeURI(competitorTrackIds)}`)
}

export function exportBulk({ trackId, countryCode = 'ALL', device, startDate, endDate, targetLanguage, competitorIds }) {
  return makeRequest(`/account/app/${trackId}/keywords/export/${countryCode}?${paramsToQuery({
    startDate,
    endDate,
    device,
    targetLanguage,
    competitorIds,
  })}`, {
    method: 'POST',
  })
}

// suggested, related, trending calls are made using same params object so don't change param names or structure unless necessary
export function getTrending({ countryCode }) {
  return makeRequest(`/keyword/hint/trending/country/${countryCode}`)
}

/**
 * Returns trend keywords for the given country and category (iphone is used for device)
 * @param store = ios since it is only available for ios
 * @param trackId
 * @param countryCode
 * @param category
 * @param scoreField
 * @param sortField
 * @body metadataRequestInfo - fields to be included in the response (DESCRIPTION, TITLE, SUB_TITLE, SHORT_DESCRIPTION) and includeNGrams
 * ex.: {"fields":["DESCRIPTION","TITLE","SHORT_DESCRIPTION"],"includeNGrams":true} returns keywords for all fields.
 * @returns {Promise<any>}
 */
export function getLatestTrendKeywords({ store, trackId, countryCode, category, sortColumn, order, includeNGrams, fields }) {
  return makeRequest(`keyword/trends/${store}/country/${countryCode}/latest-top-keywords/${trackId}?${paramsToQuery({
    category,
    scoreField: sortColumn,
    sortField: order,
  })}`, {
    method: 'POST',
    data: {
      metadataRequestInfo: {
        includeNGrams,
        fields,
      },
    },
  })
}

/**
 * Returns keywords trend category.
 * @param countryCode
 * @param scoreField
 */
export function getTrendsCategory({ countryCode, scoreField }) {
  return makeRequest(`keyword/trends/category-stats/${countryCode}?${paramsToQuery({
    scoreField,
  })}`)
}

export function getKeywordsSearchScore({ storeName, countryCode, keywords }) {
  return makeRequest(`/app/${storeName}/keywords-search-score/${countryCode}`, {
    method: 'POST',
    data: keywords,
  })
}

export function getBrand({ trackId, countryCode }) {
  return makeRequest(`/app/${trackId}/brand-app-keywords/${countryCode}/v2`)
}

export function getSuggested({ store, keyword, countryCode }) {
  return makeRequest(`/keyword/hint/suggested-with-volume/store/${store}/country/${countryCode}?${paramsToQuery({ keyword })}`)
}

export function getSimilar({ store, countryCode, keyword }) {
  return makeRequest(`/keyword-metrics/similar-keywords/store/${store}/${countryCode}?keywords=${encodeURIComponent(keyword)}`)
}

export function getRecommended({ countryCode, keyword }) {
  return makeRequest(`/keyword/popularity/recommended/${countryCode}?keyword=${encodeURIComponent(keyword)}`)
}

export function getKeywordCountsByCountryForApp() {
  return makeRequest(`/account/keywords/count-by-app`)
}

export function deleteKeyword({ trackId, countryCode }, keywords) {
  let _keywords = keywords || []
  if (keywords && typeof keywords === 'string') {
    _keywords = [keywords]
  }
  return makeRequest(`/account/app/${trackId}/keywords/${countryCode}`, {
    method: 'DELETE',
    data: JSON.stringify(_keywords),
  })
}

export function deleteAllByRegion(trackId, countryCode) {
  return makeRequest(`/account/app/${trackId}/keywords/all?${paramsToQuery({ countryCode })}`, {
    method: 'DELETE',
  })
}

export function deleteAll() {
  return makeRequest('/account/keywords/delete-all', { method: 'DELETE' })
}

export function updateKeywordFields({ trackId, countryCode, keyword, note, color }) {
  return makeRequest(`/account/app/${trackId}/keywords/${countryCode}?${paramsToQuery({
    keyword,
    note,
    color,
  })}`, { method: 'PUT' })
}

export function getTopKeywordsAnalysis({ trackId, region }) {
  return makeRequest(`/app/${trackId}/top-keywords/${region}`)
}

export function getKeywordsByDownloads({ trackId, countryCode, device }) {
  return makeRequest(`/app/${trackId}/keywords-by-downloads/${countryCode}/v2?${paramsToQuery({ device })}`)
}

export function getKwIntelligenceKeywordsByDownloads({ trackId, countryCode, device, date }, axiosCfg) {
  return makeRequest(`/app/${trackId}/keyword-intelligence/keywords-by-downloads/${countryCode}?${paramsToQuery({
    device,
    date,
  })}`, axiosCfg)
}

export function getKwIntelligenceKeywordsByDownloadsWeekly({ trackId, countryCode, device, date }, axiosCfg) {
  return makeRequest(`/keyword-intelligence/downloads/weekly/${trackId}/${countryCode}?${paramsToQuery({
    device,
    date,
  })}`, axiosCfg)
}

/**
 * Returns the translated keywords according to the given sourceLanguage
 * @param store store to translate
 * @param keywords source keywords
 * @param sourceLanguage source language
 * @param targetLanguages target languages
 * @param axiosCfg - for request cancelling
 * @returns {Promise} List of translated keywords
 */
export function getTranslatedKeywordsBySourceLanguage({ store, keywords, sourceLanguage, targetLanguages }, axiosCfg) {
  return makeRequest(`/translate/keywords?store=${store}&language=${sourceLanguage}`, {
    ...axiosCfg,
    method: 'POST',
    data: {
      keywords,
      targetLanguages,
    },
  })
}

export function getSearchScoreHistory({ keyword, countryCode: country, startDate, endDate, store }) {
  return makeRequest(`/keyword/search-score-history/store/${store}/country/${country}?${paramsToQuery({
    keyword,
    startDate,
    endDate,
  })}`)
}

export function getDescriptionKeywords({ data, langCode, isTitleText }) { // isTitleText:true - return not sorted and
  return makeRequest(`/description-frequency/calculate?isTitleText=${isTitleText}`, {
    method: 'POST',
    data: {
      description: data,
      langCode,
    },
  })
}

export function getKeywordMetadata({ storeName, countryCode, device, keyword }) {
  return makeRequest(`/keyword/metadata/store/${storeName}/v2?${paramsToQuery({
    countryCode,
    device,
    keyword,
  })}`)
}

export function getKeywordMetadataMultiple({ storeName, countryCode, device }, keywords) {
  if (!keywords?.length) {
    return Promise.reject(MalformedParamException)
  }
  return makeRequest(`/keyword/metadata/store/${storeName}/multiple?${paramsToQuery({
    countryCode,
    device,
  })}`, {
    method: 'POST',
    data: keywords,
  })
}

export function getCleanedKeywordFromSpecialCharacters(data) {
  return makeRequest(`/keyword/cleanSpecialCharacters`, {
    method: 'POST',
    data,
  })
}

export function getCategoryKeywords({ storeName, categoryId, countryCode, device }) {
  return makeRequest(`/keyword-metrics/category-keywords/store/${storeName}/${countryCode}/${categoryId}/v2?${paramsToQuery({ device })}`)
}

export function getRankedOrganicKeywords({ trackId, countryCode, device, date }) {
  return makeRequest(`/app/${trackId}/organic-keywords/${countryCode}/v2?device=${device}&date=${date}`)
}

export function getRankedPaidKeywords({ trackId, countryCode, startDate, endDate }) {
  return makeRequest(`/app/${trackId}/paid-keywords/${countryCode}/v2?startDate=${startDate}&endDate=${endDate}`)
}

export function getKeywordCompetition({ trackId, countryCode, device, date }) {
  return makeRequest(`/account/app/${trackId}/keywords/competition/${countryCode}?${paramsToQuery({
    device,
    date,
  })}`)
}

export function getKeywordCompetitionSingleKeywordWithDate({
  trackId,
  countryCode,
  device,
  keyword,
  startDate,
  endDate,
}) {
  return cacheRequest(`/account/app/${trackId}/keywords/competition/single/${countryCode}?${paramsToQuery({
    keyword,
    device,
    startDate,
    endDate,
  })}`)
}

export function getSearchAdsRecommendedKeywords({ trackId, countryCode }) {
  return makeRequest(`/app/${trackId}/country/${countryCode}/popularity/recommended-keywords`)
}

export function getKeywordCountByCountry(trackId) {
  return makeRequest(`/account/app/${trackId}/keywords/count-by-country`)
}

export function getLiveKeywordRankings({ storeName, countryCode, keyword, device }) {
  return cacheRequest(`/keyword/live-rankings/store/${storeName}/country/${countryCode}?${paramsToQuery({
    keyword,
    device,
  })}`, {}, { cacheTimeout: HOUR_IN_MS })
}

export function getKeywordAppsForCountry({ storeName, countryCode, keyword }) {
  return makeRequest(`/keyword/keyword-apps?${paramsToQuery({ storeName, countryCode, keyword })}`)
}

export function getPlayKeywordsWithInstalls({ trackId, startDate, endDate }) {
  return makeRequest(`/account/store-account/app/${trackId}/data-history/play-keyword-installs?${
    paramsToQuery({ startDate, endDate })}`)
}

export function getShareOfVoiceForCategory({ store, keyword, countryCode, device }) {
  return makeRequest(`/keyword-metrics/share-of-category/store/${store}/keyword/${keyword}/${countryCode}?device=${device}`)
}

export function getShareOfVoiceForApp({ store, keyword, countryCode, device }) {
  return makeRequest(`/keyword-metrics/share-of-voice/store/${store}/keyword/${keyword}/${countryCode}?device=${device}`)
}

export function getTranslatedAppKeywords({ trackId, countryCode }) {
  return makeRequest(`/account/app/${trackId}/keywords/translated/${countryCode}`)
}

export function getKeywordsSearchAdsData({ keywords, trackId, countryCode, startDate, endDate }) {
  return makeRequest(`/sa/bid-keyword/${trackId}/${countryCode}?${paramsToQuery({
    startDate,
    endDate,
  })}`, {
    method: 'POST',
    data: keywords,
  })
}

export function getAISuggestedKeywords({ trackId, countryCode, limit }) {
  return makeRequest(`/keyword/ai/keyword-suggestions/${trackId}/${countryCode}?limit=${limit}`)
}

/**
 * Returns translated keywords list with using google translate.
 * @param countryCode
 * @param keywords
 * @returns {Promise<any>}
 */
export function getEnglishTranslationsFromGoogle({ keywords, countryCode }) {
  return makeRequest(`/translate/keywords/${countryCode}`, {
    method: 'POST',
    data: keywords,
  })
}

/**
 * Returns translated keywords list from db.
 * @param countryCode
 * @param store
 * @param keywords
 * @returns {Promise<any>}
 */
export function getTranslatedKeywords({ countryCode, storeName, keywords }) {
  return makeRequest(`/translate/${storeName}/keywords/${countryCode}`, {
    method: 'POST',
    data: keywords,
  })
}

/**
 * Translate keywords.
 * @param sourceCountryCode
 * @param trackId
 * @param targetLanguageCode
 * @param keywords
 */
export function translateKeywords({ keywords, trackId, sourceCountryCode, targetLanguageCode }) {
  return makeRequest(`/translate/keywords/${trackId}/${sourceCountryCode}?target=${targetLanguageCode}`, {
    method: 'POST',
    data: keywords,
  })
}

export function getUntrackedKeywordRanking({ trackId, keywords, countryCode, device, startDate, endDate }) {
  return makeRequest(`/account/app/${trackId}/keywords/ranking-untracked/${countryCode}?${paramsToQuery({
    device,
    startDate,
    endDate,
  })}`, {
    method: 'POST',
    data: keywords,
  })
}

export function getUntrackedKeywordRankings({ trackId, keywords, countryCode, device, startDate, endDate }) {
  return makeRequest(`/app/${trackId}/keywords/ranking/${countryCode}?${paramsToQuery({
    device,
    startDate,
    endDate,
  })}`, {
    method: 'POST',
    data: keywords,
  })
}

/**
 * Returns app info related keywords search score. Contains title, subtitle, description, short description search scores.
 * @param trackId
 * @param countryCode
 * @param props: specifies which fields' keywords will be returned.
 * ex.: {"fields":["DESCRIPTION","TITLE","SHORT_DESCRIPTION"],"includeNGrams":true} returns keywords for all fields.
 * @returns {Promise<*>} List of keywords with search scores
 */
export function getAppInfoKeywordsSearchScore({ trackId, countryCode, includeNGrams, fields }) {
  return makeRequest(`/app/${trackId}/keywords/search-score/${countryCode}`, {
    method: 'POST',
    data: {
      includeNGrams,
      fields,
    },
  })
}

/**
 * Returns app related keywords . Contains title, subtitle, description, short description search scores.
 * @param trackId
 * @param countryCode
 * @returns {Promise<*>} List of keywords
 */
export function getAppKeywords({ trackId, countryCode }) {
  return makeRequest(`/app/onboarding/app/${trackId}/keywords/${countryCode}`, {
    method: 'GET',
  })
}

/**
 * Returns keyword spot estimations for the given appstore app
 * @param trackId ios only
 * @param countryCode
 * @returns {Promise<*>} List of keywords with search scores
 */
export function getKeywordSpotEstimationsV2({ trackId, countryCode }) {
  return makeRequest(`/app/${trackId}/keyword-estimations/${countryCode}/search-score`)
}

/**
 * Returns keyword spots for the given appstore app according to date filter defined between startDate and endDate
 * @param trackId ios only
 * @param countryCode
 * @returns {Promise<*>} List of keywords with locale, date and search score.
 */
export function getKeywordSpotHistoryV2({ trackId, countryCode, startDate, endDate }) {
  return makeRequest(`/account/store-account/app/${trackId}/data-history/keyword-spot/search-score?${paramsToQuery({
    startDate,
    endDate,
    countryCode,
  })}`)
}

/**
 * Returns keyword spots for the given appstore app
 * @param trackId ios only
 * @param countryCode
 * @returns {Promise<*>} List of keywords with locale, date and search score.
 */
export function getKeywordSpotV2({ trackId, countryCode }) {
  return makeRequest(`/account/store-account/app/${trackId}/data/keyword-spot/search-score?${paramsToQuery({ countryCode })}`)
}

/**
 * Returns rank and search score for the given keywords. Also ascend sorted by rank.
 * @param trackId
 * @param countryCode
 * @param device
 * @param keywords
 * @returns {Promise<*>} List of keywords with rank and search score.
 */
export function getKeywordsWithRankAndSearchScore({ trackId, countryCode, device, keywords }) {
  return makeRequest(`sa/stats/keyword-ranking-sorted/app/${trackId}/${countryCode}?device=${device}`, {
    method: 'POST',
    data: keywords,
  })
}

/**
 * Returns predictions for each keyword change in metadata.
 * @param store
 * @param trackId
 * @param countryCode
 * @param payload
 * {
    "oldTitleKeywords": ["kw1"],
    "newTitleKeywords": [kw2"],
    "oldSubTitleKeywords": ["kw3"],
    "newSubTitleKeywords": ["kw4", "kw5", "kw3"],
    "oldKeywordSpotKeywords": ["kw6", "kw7"],
    "newKeywordSpotKeywords": ["kw1", "kw7"]
    }
 * @returns {Promise<*>} List of keywords with score and confidence score.
 */
export function getKeywordMetadataForecast({ store, trackId, countryCode }, payload) {
  return makeRequest(`datascience/keyword-forecast/store/${store}/app/${trackId}?country=${countryCode}`, {
    method: 'POST',
    data: payload,
  })
}

/**
 * Retrieves the app keyword ranking from the specified parameters.
 *
 * @param {object} options - The options for the request.
 * @param {string} options.country - The country code (ISO Alpha-2) to get rankings for.
 * @param {number} options.trackId - The unique identifier for the app.
 * @param {string} options.startDate - The start date for the ranking period (YYYY-MM-DD).
 * @param {string} options.endDate - The end date for the ranking period (YYYY-MM-DD).
 * @param {string} options.device - The type of device to get rankings for (e.g., 'iphone', 'ipad').
 * @param {string} options.spikeDirection - The direction of the ranking spike ('ASC' or 'DESC').
 * @returns {Promise} A Promise that resolves to the response from the request.
 */

export function getAppKeywordRankings({ countryCode, trackId, startDate, endDate, device, spikeDirection }) {
  return makeRequest(`/app/${trackId}/${countryCode}/rankings-with-search-score/ranking/${spikeDirection}?startDate=${startDate}&endDate=${endDate}&device=${device}`)
}

/**
 * Retrieves the app keyword popularity from the specified parameters.
 *
 * @param {object} options - The options for the request.
 * @param {string} options.country - The country code (ISO Alpha-2) to get rankings for.
 * @param {number} options.trackId - The unique identifier for the app.
 * @param {string} options.startDate - The start date for the ranking period (YYYY-MM-DD).
 * @param {string} options.endDate - The end date for the ranking period (YYYY-MM-DD).
 * @param {string} options.device - The type of device to get rankings for (e.g., 'iphone', 'ipad').
 * @param {string} options.spikeDirection - The direction of the ranking spike ('ASC' or 'DESC').
 * @returns {Promise} A Promise that resolves to the response from the request.
 */

export function getAppKeywordPopularityScores({ countryCode, trackId, startDate, endDate, spikeDirection, device }) {
  return makeRequest(`/app/${trackId}/${countryCode}/ranked-keywords/search-score/${spikeDirection}?startDate=${startDate}&endDate=${endDate}&device=${device}`)
}
